import type {
	GamePageResponse,
	GPGamePlayerResponse,
	GPGameTeamResponse,
	PlayerGradingSubmitPayload,
	PlayerRatingModalData,
	RatingPutRequest,
} from "../../types";
import { ButtonTabs, type ButtonTabsItem } from "ui-kit";
import { useState } from "react";
import "./TeamSheet.css";
import { _ } from "i18n";
import { TeamSheetPlayersTable } from "./TeamSheet/TeamSheetPlayersTable";
import { useEffect } from "react";
import { PlayerRatingModal } from "./TeamSheet/PlayerRatingModal";
import { useFullscreenModalContainer } from "../../useFullscreenModalContainer";
import { Button} from "react-aria-components";
import clsx from "clsx";

interface TeamSheetProps {
	gameData: GamePageResponse;
	handleOpenEditTeamSheetModal: (team: GPGameTeamResponse) => void;
	handleSetPlayerRating?: (id: number, rating: RatingPutRequest) => void;
	handleSubmitPlayerGrading?: (payload: PlayerGradingSubmitPayload) => void | Promise<void>;
	toastMessage: (type: string, message: string) => void;
}

function getClubsArray(gameData: GamePageResponse) {
	const homeClub = gameData.homeGameTeam;
	const awayClub = gameData.awayGameTeam;

	const clubsArray: ButtonTabsItem[] = [];

	if (homeClub) {
		clubsArray.push({
			key: homeClub.id,
			label: _("label.match.homeTeam"),
			image: homeClub.logo,
			imageShape: "round",
		});
	}

	if (awayClub) {
		clubsArray.push({
			key: awayClub.id,
			label: _("label.match.awayTeam"),
			image: awayClub.logo,
			imageShape: "round",
		});
	}

	return clubsArray;
}

function getTeamsheetPlayers(gameTeam: GPGameTeamResponse): GPGamePlayerResponse[] {
	if (gameTeam.teamsheetHidden === true) {
		return [];
	}

	return gameTeam.players;
}

//remove use effects and use states -> can be replaced with just plain vars
export function TeamSheet({
	gameData,
	handleOpenEditTeamSheetModal,
	handleSetPlayerRating,
	handleSubmitPlayerGrading,
	toastMessage,
}: TeamSheetProps) {
	const [clubs] = useState<ButtonTabsItem[]>(() => getClubsArray(gameData));
	const fullscreenModalContainer = useFullscreenModalContainer();

	const [team, setTeam] = useState<number | null>(() => {
		return gameData.homeGameTeam ? gameData.homeGameTeam.id : null;
	});
	const [gameTeam, setGameTeam] = useState<GPGameTeamResponse | null>(() => {
		return gameData.homeGameTeam;
	});

	const [teamSheetButtonText, setTeamSheetButtonText] = useState<string>(() => {
		let text = _("match.add_matchsheet");

		if ((gameTeam !== null && getTeamsheetPlayers(gameTeam).length > 0) || getTeamsheetPlayers(gameData.homeGameTeam).length > 0) {
			text = _("match.edit_matchsheet");
		}

		return text;
	});

	const [players, setPlayers] = useState<GPGamePlayerResponse[]>(() => getTeamsheetPlayers(gameData.homeGameTeam));
	const [canEditTeamSheet, setEditTeamSheetPermission] = useState<boolean>(gameTeam?.permissions?.editTeamSheet ?? false);

	const initModalData = {
		id: 0,
		show: false,
		name: null,
		rating: {
			score: 0,
			visibility: null,
			ratedBy: null,
			ratedAt: null,
		},
	};

	const [playerRatingModal, setPlayerRatingModal] = useState<PlayerRatingModalData>(initModalData);

	const handleOnTeamChange = (key: (string | number)[]) => {
		const team = key[0] as number;
		setTeam(team);

		let text = _("match.add_matchsheet");

		if (team === gameData.homeGameTeam.id) {
			setGameTeam(gameData.homeGameTeam);
			setPlayers(getTeamsheetPlayers(gameData.homeGameTeam));
			if (getTeamsheetPlayers(gameData.homeGameTeam).length > 0) {
				text = _("match.edit_matchsheet");
			}
			setEditTeamSheetPermission(gameData.homeGameTeam.permissions.editTeamSheet);
		} else {
			setGameTeam(gameData.awayGameTeam);
			setPlayers(getTeamsheetPlayers(gameData.awayGameTeam));
			if (getTeamsheetPlayers(gameData.awayGameTeam).length > 0) {
				text = _("match.edit_matchsheet");
			}
			setEditTeamSheetPermission(gameData.awayGameTeam.permissions.editTeamSheet);
		}
		setTeamSheetButtonText(text);
	};

	useEffect(() => {
		if (team === gameData.homeGameTeam.id) {
			setGameTeam(gameData.homeGameTeam);
			setPlayers(getTeamsheetPlayers(gameData.homeGameTeam));
			setEditTeamSheetPermission(gameData.homeGameTeam.permissions.editTeamSheet);
		} else {
			setGameTeam(gameData.awayGameTeam);
			setPlayers(getTeamsheetPlayers(gameData.awayGameTeam));
			setEditTeamSheetPermission(gameData.awayGameTeam.permissions.editTeamSheet);
		}
	}, [gameData, team]);

	return (
		<div className="teamSheet-container">
			<div className="teamSheet-container__segment-container">
				<div className="teamSheet-container__segmented-button">
					{<ButtonTabs items={clubs} active={team ? [team] : []} onChange={handleOnTeamChange} />}
				</div>
			</div>

			<TeamSheetPlayersTable
				players={players.filter((player) => player.isSubstitute === false)}
				permissions={gameData.permissions}
				gameTeam={gameTeam}
				setPlayerRatingModal={setPlayerRatingModal}
				onSubmitPlayerGrading={handleSubmitPlayerGrading}
				toastMessage={toastMessage}
			/>
			<TeamSheetPlayersTable
				gameTeam={gameTeam}
				setPlayerRatingModal={setPlayerRatingModal}
				onSubmitPlayerGrading={handleSubmitPlayerGrading}
				toastMessage={toastMessage}
				players={players.filter((player) => player.isSubstitute === true)}
				permissions={gameData.permissions}
				tableName={_("match.replacementteam")}
			/>
			<PlayerRatingModal
				key={`rating-${playerRatingModal.show}-${playerRatingModal.id}`}
				modalData={playerRatingModal}
				close={() => setPlayerRatingModal(initModalData)}
				onSubmit={handleSetPlayerRating}
				UNSTABLE_portalContainer={fullscreenModalContainer}
			/>
			{canEditTeamSheet && gameTeam && gameTeam.teamsheetHidden !== true && (
				<div className="team-sheet__footer">
					<Button className={clsx("button")} onPress={() => handleOpenEditTeamSheetModal(gameTeam)}>
						{teamSheetButtonText}
					</Button>
				</div>
			)}
		</div>
	);
}
